import React, { useEffect, useState } from 'react';
import { Alert, Form, message, Radio } from 'antd';

import { DatePicker, InputNumber } from 'components/Form';
import { Button, Modal, Message } from 'ui';

import { config } from 'config/config';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';

type Mode = 'days' | 'date' | 'program';

interface UpdateExpireDateModalProps {
    visible: boolean;
    selectedIds: Array<number | string>;
    onCancel: () => void;
    onSuccess: () => void;
}

const formLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 14 },
};

const UpdateExpireDateModal: React.FC<UpdateExpireDateModalProps> = ({
    visible,
    selectedIds,
    onCancel,
    onSuccess,
}) => {
    const t = useT();
    const [form] = Form.useForm();
    const [mode, setMode] = useState<Mode>('days');

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setMode('days');
        }
    }, [visible, form]);

    const { appendData, isLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: '/api/v1/courses/certificates/expiration-date',
            method: 'PUT',
        },
        onSuccess: () => {
            onSuccess();
        },
    });

    const submit = (values: any) => {
        const payload: Record<string, unknown> = { ids: selectedIds };

        if (mode === 'days') {
            payload.days = values.days;
        } else if (mode === 'date') {
            payload.expirationDate = values.expirationDate.format(config.DBDateFormat);
        } else {
            payload.useProgramSettings = true;
        }

        appendData(payload);
    };

    return (
        <Modal
            open={visible}
            title={t('general.update_expire_date')}
            onCancel={onCancel}
            onOk={() => form.submit()}
            isLoading={isLoading}
            footer={[
                <Button key="cancel" onClick={onCancel} message="general.cancel" />,
                <Button
                    key="ok"
                    type="primary"
                    loading={isLoading}
                    onClick={() => form.submit()}
                    message="general.save"
                />,
            ]}
        >
            <Form form={form} onFinish={submit} {...formLayout}>
                <Form.Item label={t('general.mode')}>
                    <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
                        <Radio value="days">{t('general.days_after_issue')}</Radio>
                        <Radio value="date">{t('general.expiration_date')}</Radio>
                        <Radio value="program">{t('general.use_program_settings')}</Radio>
                    </Radio.Group>
                </Form.Item>

                {mode === 'days' && (
                    <InputNumber
                        name="days"
                        label={t('general.days')}
                        min={1}
                        initialValue={null}
                        customRules={[{ required: true, message: t('validation.field_required') }]}
                        customLayout={formLayout}
                    />
                )}

                {mode === 'date' && (
                    <DatePicker
                        name="expirationDate"
                        label={t('general.expiration_date')}
                        format={config.defaultDateFormat}
                        customRules={[{ required: true, message: t('validation.field_required') }]}
                        customLayout={formLayout}
                    />
                )}

                {mode === 'program' && (
                    <Alert
                        message={t('general.use_program_settings_hint')}
                        type="info"
                    />
                )}
            </Form>
        </Modal>
    );
};

export default UpdateExpireDateModal;
